import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  EquisDropdownTypes,
  EquisDropdownWidth,
  EquisDropdownDisplay,
  IEquisDropdownEmission,
  IEquisDropdown,
} from '@tn-equis/core/components/dropdown';
import { EquisButtonBaseSizes } from '@tn-equis/core/components/button';

import { INavbarTop, DropdownProps } from './navbar-top.types';
import { ActiveTabService } from '../active-tab.service';
import { SectionService } from 'src/app/shared/services/section.service';
import { NX23Link } from 'src/app/shared/types/content-types';
import { ChatbotService } from 'src/app/shared/services/chatbot.service';
import { DataLayer } from 'src/app/shared/types/contentful';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Component({
  selector: 'nx-navbar-top',
  templateUrl: './navbar-top.component.html',
  styleUrls: ['./navbar-top.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarTopComponent implements INavbarTop, OnInit {
  constructor(
    private section: SectionService,
    public activeTab: ActiveTabService,
    private chatbot: ChatbotService,
    private router: Router,
    private location: Location,
  ) {}

  // REQUIRED INPUTS
  @Input() data!: INavbarTop['data'];

  // OUTPUTS
  @Output() menuToggleEvent: INavbarTop['menuToggleEvent'] = new EventEmitter();

  // PROPERTIES
  helpDropdownProps!: DropdownProps;
  isMenuOpen: boolean = false;
  showBackButton: boolean = true;

  readonly DOWNLOAD_APP_SIZE = EquisButtonBaseSizes.MEDIUM;
  readonly SIGNIN_SIZE = EquisButtonBaseSizes.MEDIUM;
  private readonly CHATBOT_TEXT = 'Chat';
  // Empty because it shouldn't redirect to any page
  private readonly CHATBOT_LINK = '';

  // METHODS
  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.menuToggleEvent.emit(this.isMenuOpen);
  }

  goBack(): void {
    this.location.back();
  }

  onDropdownItemClick(item: IEquisDropdownEmission): void {
    const index = item.globalIndex;
    const dropdownItem =
      this.helpDropdownProps.dropdownList[index].optionList[0];

    if (dropdownItem.isBot) {
      this.chatbot.show();
    }
  }

  // PRIVATE METHODS
  /**
   * Generates a single dropdown item.
   * @param item Item to generate the dropdown item. If it is undefined, it will generate the chatbot item.
   * @returns EquisDropdown object
   */
  private generateDropdownItem(item?: NX23Link): IEquisDropdown {
    return {
      optionList: [
        {
          text: item ? item.fields.label : this.CHATBOT_TEXT,
          link: item ? item.fields.url : this.CHATBOT_LINK,
          // If the item is the chatbot, it should open in the same tab
          openSameTab: true,
          type: EquisDropdownTypes.LINK,
          fontWeight: '900',
          textColor: '#333333',
          // Although dataLayer is not necessary to render the dropdown, it is necessary in the onClick event
          dataLayer: item?.fields?.dataLayer,
          isBot: !item,
        },
      ],
    };
  }
  /**
   * Generates the dropdown list.
   * @returns Dropdown list
   */
  private generateDropdownList(): IEquisDropdown[] {
    const dataArray = this.data.helpDropdown ?? [];
    const chatbot = this.generateDropdownItem();
    const array = dataArray.map(item => this.generateDropdownItem(item));

    return [chatbot, ...array];
  }
  /**
   * Initializes the dropdown props.
   */
  private initDropdownProps(): void {
    this.helpDropdownProps = {
      dropdownDirection: EquisDropdownDisplay.BOTTOM_LEFT,
      marginTop: 4,
      marginSide: 8,
      widthType: EquisDropdownWidth.PIXELS,
      widthPixels: 300,
      closeOnClick: true,
      dropdownList: this.generateDropdownList(),
    };
  }

  private checkUrl(url: string): void {
    if (url === '/ayuda' || url === '/') {
      this.showBackButton = false;
    } else {
      this.showBackButton = true;
    }
  }

  private initShowBackButton(): void {
    // Subscribe to router events to check the current URL
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event: NavigationEnd) => {
        this.checkUrl(event.urlAfterRedirects);
      });

    // Initial check for when the component is first loaded
    this.checkUrl(this.router.url);
  }

  // HOOKS
  ngOnInit(): void {
    this.initDropdownProps();
    this.initShowBackButton();
  }
}
